import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { INode, Dictionary, Data } from '../shared/types';
import { Utils } from '../shared/utils';
import { DataService } from './data.service';
import { CONFIG, VIEWS } from '../shared/providers';

@Injectable({
  providedIn: 'root',
})
export class ExplorerService {
  private dataService: DataService = inject(DataService);
  private config = inject(CONFIG);
  private views = inject(VIEWS);
  private internalTree = Utils.createNode(this.config.homeNodeName || 'Home', 0, false, {}, true);
  private flatPointers: Dictionary<INode> = { [this.internalTree.id]: this.internalTree };
  private loading$$ = new BehaviorSubject<boolean>(false);

  private readonly selectedNodes$$ = new BehaviorSubject<INode[]>([]);
  private readonly openFile$$ = new BehaviorSubject<INode | undefined>(undefined);
  private readonly openedNode$$ = new BehaviorSubject<INode | undefined>(undefined);
  private readonly root$$ = new BehaviorSubject<INode>(this.internalTree);
  // public readonly currentView$ = new BehaviorSubject<string>(this.config.defaultView || this.views[0].name);
  public readonly currentView$: BehaviorSubject<string>;


  private readonly homeButton$$ = new BehaviorSubject<boolean>(this.config.homeTreeLink || false);

  /**
   * An Observable that emits the home button state.
   * Subscribers can use this to react to changes in the home button state.
   */
  public readonly homeButton$ = this.homeButton$$.asObservable();
  /**
    * An Observable that emits the loading state of the explorer.
    * Subscribers can use this to react to changes in the loading state.
    */
  public readonly loading$ = this.loading$$.asObservable();

  /**
   * An Observable that emits the currently selected nodes in the explorer.
   * Subscribers can use this to react to changes in the selection.
   */
  public readonly selection$ = this.selectedNodes$$.asObservable();

  /**
   * An Observable that emits the currently opened directory in the explorer.
   * Subscribers can use this to react to changes in the opened directory.
   */
  public readonly openedDir$ = this.openedNode$$.asObservable();

  /**
     * An Observable that emits the currently opened file in the explorer.
     * Subscribers can use this to react to changes in the opened file.
     */
  public readonly openFile$ = this.openFile$$.asObservable();

  /**
   * An Observable that emits the root node of the explorer.
   * Subscribers can use this to react to changes in the root node.
   */
  public readonly root$ = this.root$$.asObservable();

  constructor() {

    // Define the localStorage key as a constant
    const STORAGE_KEY = 'xrsupport_currentView';
    const storedView = localStorage.getItem(STORAGE_KEY);
    const initialView = storedView || this.config.defaultView || this.views[0].name;

    // Inizializzo il BehaviorSubject con il valore scelto
    this.currentView$ = new BehaviorSubject<string>(initialView);

    // Ogni volta che cambia il valore, salvo nel localStorage
    this.currentView$.subscribe(view => {
      if (view) {
        localStorage.setItem(STORAGE_KEY, view);
      };
    });
  }

  /**
   * Returns the node with the given id.
   * @param id The id of the node to retrieve.
   */
  public getNode(id: number) {
    return this.flatPointers[id];
  }

  /**
   * Sets the selected nodes in the explorer.
   * @param nodes The nodes to select.
   */
  public select(nodes: INode[]) {
    this.selectedNodes$$.next(nodes);
  }

  public openFile(node: INode | undefined) {
    let sel = node ? node : undefined;
    this.openFile$$.next(sel);
  }

  /**
   * Opens the node with the given id. If no id is provided, the root node is opened.
   * @param id The id of the node to open.
   */
  public openNode(id?: number) {
    if (!id) {
      id = this.internalTree.id;
    }

    this.getContent(id).subscribe(() => {
      const parent = this.flatPointers[id!];
      this.openedNode$$.next(parent);
      this.selectedNodes$$.next([]);
    });
  }

  /**
   * Expands the node with the given id.
   * @param id The id of the node to expand.
   */
  public expand(id: number) {
    const parent = this.flatPointers[id];
    parent.expanded = true;
    this.getContent(id).subscribe();
  }

  public collapse(id: number) {
    const parent = this.flatPointers[id];
    parent.expanded = false;
    this.root$$.next(this.internalTree);
  }

  /**
   * Creates a new directory with the given name in the currently opened directory.
   * @param name The name of the new directory.
   */
  public createDir(name: string) {
    if (this.config.features?.createDir === false) {
      throw new Error('Create directory feature is disabled');
    }

    const parent = this.openedNode$$.value;
    this.dataService.createDir(parent!.data, name).subscribe(() => {
      this.refresh();
    });
  }

  /**
   * Refreshes the currently opened node.
   */
  public refresh() {
    this.openNode(this.openedNode$$.value!.id);
  }

  /**
   * Renames the currently selected node.
   * @param name The new name for the node.
   */
  public rename(name: string) {
    if (this.config.features?.rename === false) {
      throw new Error('Rename feature is disabled');
    }

    const nodes = this.selectedNodes$$.value;
    if (nodes.length > 1) {
      throw new Error('Multiple selection rename not supported');
    }
    if (nodes.length === 0) {
      throw new Error('Nothing selected to rename');
    }

    const node = nodes[0];
    this.dataService.rename(node.data, name).subscribe(() => {
      this.refresh();
    });
  }

  public moveItemToFolder(data: Data, parentId: number) {
    return this.dataService.moveitem(data, parentId).subscribe(() => {
      this.refresh();
    });
  }

  /**
   * Removes the currently selected nodes.
   */
  public remove() {
    if (this.config.features?.delete === false) {
      throw new Error('Delete feature is disabled');
    }

    const selection = this.selectedNodes$$.value;
    if (selection.length === 0) {
      throw new Error('Nothing selected to remove');
    }

    const targets = selection.map((node) => this.flatPointers[node.id].data);
    this.dataService.delete(targets).subscribe(() => {
      this.refresh();
    });
  }

  /**
   * Uploads the given files to the currently opened directory.
   * @param files The files to upload.
   */
  public upload(files: FileList) {
    if (this.config.features?.upload === false) {
      throw new Error('Upload feature is disabled');
    }

    const node = this.openedNode$$.value!;
    this.dataService.uploadFiles(node.data, files).subscribe(() => {
      this.refresh();
    });
  }

  /**
   * Downloads the currently selected file.
   */
  public download() {
    if (this.config.features?.download === false) {
      throw new Error('Download feature is disabled');
    }

    const target = this.selectedNodes$$.value[0];
    this.dataService.downloadFile(target.data).subscribe(() => {
      this.refresh();
    });
  }

  /**
   * Open node and get all parent nodes
   * Ideal for opening a previously opened node by value
   */
  public openTree(data: Data) {
    this.dataService.openTree(data).subscribe((dataNodes) => {
      const queue = [
        {
          parent: this.internalTree,
          children: dataNodes,
        },
      ];

      let lastParent = this.internalTree;
      while (queue.length > 0) {
        const { parent, children } = queue.shift()!;
        lastParent = parent;
        children.forEach((child) => {
          const node = Utils.createNode(this.dataService.getName(child.data), parent.id, child.isLeaf, child.data);
          parent.children.push(node);
          this.flatPointers[node.id] = node;
          if (!node.isLeaf && child.children && child.children.length > 0) {
            parent.expanded = true;
            queue.push({
              parent: node,
              children: child.children,
            });
          }
        });
      }

      this.root$$.next(this.internalTree);
      this.openedNode$$.next(lastParent);
      this.selectedNodes$$.next([]);
    });
  }

  public viewFile() {
    const target = this.selectedNodes$$.value[0];
    this.dataService.viewFile(target.data).subscribe(() => {
    });
  }

  public sendFile() {
    const target = this.selectedNodes$$.value[0];
    this.dataService.sendFile(target.data).subscribe(() => {
    });
  }

  public editFile() {
    const target = this.selectedNodes$$.value[0];
    this.dataService.editFile(target.data).subscribe(() => {
    });
  }

  /**
   * Invia (o reinvia) il documento indicato in analisi verso il RAG.
   * Restituisce l'Observable così che il chiamante possa gestire lo stato di
   * caricamento; al termine la lista viene aggiornata per riflettere lo stato.
   */
  public sendToRag(data: Data) {
    return this.dataService.sendToRag(data).pipe(
      tap(() => this.refresh())
    );
  }

  /**
   * Rimuove il documento indicato dalla conoscenza del RAG e aggiorna la lista.
   */
  public removeFromRag(data: Data) {
    return this.dataService.removeFromRag(data).pipe(
      tap(() => this.refresh())
    );
  }

  private getContent(id: number) {
    const parent = this.flatPointers[id];

    if (!parent) {
      throw new Error('Node not found');
    }

    if (parent.isLeaf) {
      throw new Error('Cannot open a file node');
    }

    return this.dataService.getContent(parent.data).pipe(
      tap(({ files, dirs }) => {
        const newDirNodes = dirs.map((data) => Utils.createNode(this.dataService.getName(data), id, false, data));
        const newFileNodes = files.map((data) => Utils.createNode(this.dataService.getName(data), id, true, data));
        const newChildren = newDirNodes.concat(newFileNodes);
        const added = newChildren.filter((c) => !parent.children.find((o) => Utils.compareObjects(o.data, c.data)));
        const removed = parent.children.filter((o) => !newChildren.find((c) => Utils.compareObjects(o.data, c.data)));

        removed.forEach((c) => {
          const index = parent.children.findIndex((o) => o.id === c.id);
          parent.children.splice(index, 1);
          delete this.flatPointers[c.id];
        });

        added.forEach((c) => {
          parent.children.push(c);
          this.flatPointers[c.id] = c;
        });

        const nodeChildren = parent.children.filter((c) => !c.isLeaf);
        const leafChildren = parent.children.filter((c) => c.isLeaf);
        parent.children = nodeChildren.concat(leafChildren);

        this.root$$.next(this.internalTree);
      })
    );
  }
}
